import {
  Button,
  Popconfirm,
  Popover,
} from "antd";
import { useMemo } from 'react';
import countrySettings from '../countrySettings.json';
import { useAnalytics } from '../../components/hooks/useAnalytics';

const SignButton = ({
  countryCode,
  medicalFormType,
  t,
  showConfirmReprovingValues,
  setShowConfirmReprovingValues,
  loadingConsultation,
  validateCompletedReport,
  isAuditorInfoEnabled,
  isCompanyAuditorSignerEnabled,
  form,
  isUnableToChoose,
  approvedTextList,
  medicalConsultationDetails,
  roles,
  validatedExams,
  servicesList,
  loading,
  generatedPrescriptions,
  hasDeferredExamPrescription,
}) => {
  const isMedicalDirection = roles?.includes("direccion_medica");

  const selectedStatusId = form?.getFieldValue("status") || medicalConsultationDetails?.consultation_result_id;
  const selectedStatusOption = approvedTextList?.find(option => option.id === selectedStatusId);
  const isBlockSignatureEnabled = countrySettings.medicalConsultation?.blockSignatureOnObservedWithoutPrescription?.[countryCode];
  const isObserved = selectedStatusOption?.status === 'reproved_temporally';
  const hasNoPrescription = (!generatedPrescriptions || generatedPrescriptions.length === 0) && !hasDeferredExamPrescription;
  const isBlockSignatureOnActivePrescriptionEnabled = countrySettings.medicalConsultation
    ?.blockSignatureOnActivePrescriptionWithoutObserved?.[countryCode];
  const hasActivePrescription = generatedPrescriptions?.some(prescription => prescription.prescription_type === 'prescription');
  const isSignatureBlockedByObservedNoPrescription = isBlockSignatureEnabled && isObserved && hasNoPrescription;
  const isSignatureBlockedByActivePrescriptionNotObserved = isBlockSignatureOnActivePrescriptionEnabled
    && hasActivePrescription
    && !isObserved;
  const isSignatureBlocked = isSignatureBlockedByObservedNoPrescription || isSignatureBlockedByActivePrescriptionNotObserved;

  const reprovedProceduresNames = useMemo(() => {
    const reproved = [];
    validatedExams?.forEach(exam => {
      const reprovedProcedures = exam?.procedures?.map(procedure => (procedure.result_type?.includes('reproves') || procedure.reprovingItems?.includes(procedure?.result?.toLowerCase())) && procedure.name);
      const reprovedMeasurements = exam?.measurements?.map(measurement => (measurement?.result_type?.includes('reproves') || measurement.reprovingItems?.includes(measurement?.result?.toLowerCase())) && measurement.name);
      reproved.push(...reprovedProcedures.filter(Boolean), ...reprovedMeasurements.filter(Boolean));
    });
    return [...new Set(reproved)];
  }, [validatedExams]);

  const formattedProcedures = reprovedProceduresNames.map(p => `- ${p}`).join('\n');

  const { trackEvent } = useAnalytics();

  if (isSignatureBlocked) {
    const isAuditor = (medicalFormType === 'health' && isCompanyAuditorSignerEnabled && isCompanyAuditorSignerEnabled());
    const buttonText = isAuditor ? "Firma auditor" : "Firmar";
    const buttonClass = medicalFormType === 'health' ? 'cmt-button secondary' : 'cmt-button tertiary';
    const blockedMessage = isSignatureBlockedByActivePrescriptionNotObserved
      ? t("medicalConsultations.healthForm.signatureBlockedActivePrescriptionNotObserved")
      : t("medicalConsultations.healthForm.signatureBlockedObservedNoPrescription");
    return (
      <Popover
        trigger={['hover', 'click']}
        content={blockedMessage}
        overlayClassName="sidebar-tooltip"
      >
        <span>
          <Button
            id="signButton"
            className={`${buttonClass}`}
            type="primary"
            size="large"
            disabled
          >
            {buttonText}
          </Button>
        </span>
      </Popover>
    );
  }

  const handleBlockedSignClick = () => {
    const serviceData = servicesList.find((_service) => _service.id === medicalConsultationDetails.service_id)
    trackEvent('medical_sign_error', {
      medical_consultation_id: medicalConsultationDetails?.id,
      service_type: medicalConsultationDetails?.service_type,
      failed_reason: reprovedProceduresNames.join(','),
      was_blocked: true,
      company_document_number: serviceData.company_document_number,
      service_name: serviceData.name,
    });
  };

  const handleConfirmSign = async () => {
    await form.validateFields();
    setShowConfirmReprovingValues(false);
    
    const serviceData = servicesList.find((_service) => _service.id === medicalConsultationDetails.service_id);
    trackEvent('medical_sign_error', {
      medical_consultation_id: medicalConsultationDetails?.id,
      service_type: medicalConsultationDetails?.service_type,
      failed_reason: reprovedProceduresNames.join(','),
      was_blocked: false,
      company_document_number: serviceData?.company_document_number,
      service_name: serviceData?.name,
    });

    form.setFieldsValue({ sign: true });
    form.submit();
  }

  const statusText = () => {
    const medicalConsultationStatus = approvedTextList
      ?.find(approvedText => approvedText.id === medicalConsultationDetails.consultation_result_id);
    return medicalConsultationStatus?.text;
  }

  const health = () => {
    const approvedStatusText = {
      "PE": "Apto",
      "CL": "Aprueba"
    }
    const approvedText = approvedStatusText[countryCode];

    const reprovingValues = isUnableToChoose() && statusText() === approvedText;
    const isRestricted = countrySettings.medicalConsultation.restrictedRoleToSignReprovingValues[countryCode] && !isMedicalDirection;

    const buttonText = (isCompanyAuditorSignerEnabled()) ? "Firma auditor" : "Firmar";

    if (reprovingValues) {
      if (isRestricted) {
        return (
          <Popover
            trigger={['hover', 'click']}
            content={
              <div style={{ whiteSpace: 'pre-line' }}>
                {t("medicalConsultations.healthForm.reprovingValuesRoleAlert", { procedures: formattedProcedures })}
              </div>
            }
            overlayClassName="sidebar-tooltip"
          >
            <Button
              id="signButton"
              className="cmt-button secondary"
              type="primary"
              size="large"
              onClick={handleBlockedSignClick}
            >
              {buttonText}
            </Button>
          </Popover>
        );
      }

      return (
        <Popconfirm
          icon={<span className="icon-info-rounded presign-popconfirm-icon" />}
          title={
            <div className="presign-popconfirm-title">
              <strong>{t("medicalConsultations.healthForm.titleConfirmReprovingValues")}</strong>
              <br />
              <p className="presign-popconfirm-description">
                {t("medicalConsultations.healthForm.descriptionConfirmReprovingValues")}
              </p>
            </div>
          }
          open={showConfirmReprovingValues}
          onConfirm={handleConfirmSign}
          okButtonProps={{
            className: "presign-popconfirm-button cmt-button primary",
          }}
          cancelButtonProps={{
            className: "presign-popconfirm-button cmt-button primary outline",
          }}
          okText="Si"
          cancelText="No"
          onCancel={() => setShowConfirmReprovingValues(false)}
        >
          <Button
            loading={loadingConsultation.sign || loading.criterions}
            id="signButton"
            htmlType="button"
            onClick={() => setShowConfirmReprovingValues(true)}
            className="cmt-button secondary"
            type="primary"
            size="large"
            disabled={!isAuditorInfoEnabled}
          >
            Firma auditor
          </Button>
        </Popconfirm>
      );
    }

    return (
      <Button
        loading={loadingConsultation.sign || loading.criterions}
        id="signButton"
        htmlType="submit"
        onClick={() => {
          validateCompletedReport();
        }}
        className="cmt-button secondary"
        type="primary"
        size="large"
        disabled={!isAuditorInfoEnabled}
      >
        {isCompanyAuditorSignerEnabled() ? "Firma auditor" : "Firmar"}
      </Button>
    );
  };

  const psychologicalOnePE = () => {
    return (
      (isUnableToChoose() && statusText() === 'Apto') ?
        <Popconfirm
          icon={<span className="icon-info-rounded presign-popconfirm-icon" />}
          title={<div className="presign-popconfirm-title">
            <strong>{t("medicalConsultations.healthForm.titleConfirmReprovingValues")}</strong>
            <br />
            <p className="presign-popconfirm-description">
              {t("medicalConsultations.healthForm.descriptionConfirmReprovingValues")}
            </p>
          </div>}
          open={showConfirmReprovingValues}
          onConfirm={handleConfirmSign}
          okButtonProps={{
            className: "presign-popconfirm-button cmt-button primary",
          }}
          cancelButtonProps={{
            className: "presign-popconfirm-button cmt-button primary outline",
          }}
          okText="Si"
          cancelText="No"
          onCancel={() => setShowConfirmReprovingValues(false)}
        >
          <Button
            loading={loadingConsultation.sign}
            id="signButton"
            htmlType="button"
            onClick={() => setShowConfirmReprovingValues(true)}
            className="cmt-button tertiary"
            type="primary"
            size="large"
          >
            Firma auditor
          </Button>
        </Popconfirm>
        :
        <Button
          loading={loadingConsultation.sign}
          id="signButton"
          htmlType="submit"
          onClick={() => validateCompletedReport()}
          className="cmt-button tertiary"
          type="primary"
          size="large"
        >
          Firmar
        </Button>
    );
  };

  const psychologicalOneCL = () => {
    return (
      <Button
        loading={loadingConsultation.sign}
        id="signButton"
        htmlType="submit"
        onClick={() => validateCompletedReport()}
        className="cmt-button tertiary"
        type="primary"
        size="large"
      >
        Firmar
      </Button>
    );
  };

  const psychologicalTwoCL = () => {
    return (
      <Button
        loading={loadingConsultation.sign}
        id="signButton"
        htmlType="submit"
        onClick={() => validateCompletedReport()}
        className="cmt-button tertiary"
        type="primary"
        size="large"
      >
        Firmar
      </Button>
    );
  }

  const psychologicalTwoPE = () => {
    return (
      (isUnableToChoose() && statusText() === 'Apto') ?
        <Popconfirm
          icon={<span className="icon-info-rounded presign-popconfirm-icon" />}
          title={<div className="presign-popconfirm-title">
            <strong>{t("medicalConsultations.healthForm.titleConfirmReprovingValues")}</strong>
            <br />
            <p className="presign-popconfirm-description">
              {t("medicalConsultations.healthForm.descriptionConfirmReprovingValues")}
            </p>
          </div>}
          open={showConfirmReprovingValues}
          onConfirm={handleConfirmSign}
          okButtonProps={{
            className: "presign-popconfirm-button cmt-button primary",
          }}
          cancelButtonProps={{
            className: "presign-popconfirm-button cmt-button primary outline",
          }}
          okText="Si"
          cancelText="No"
          onCancel={() => setShowConfirmReprovingValues(false)}
        >
          <Button
            loading={loadingConsultation.sign}
            id="signButton"
            htmlType="button"
            onClick={() => setShowConfirmReprovingValues(true)}
            className="cmt-button tertiary"
            type="primary"
            size="large"
          >
            Firma auditor
          </Button>
        </Popconfirm>
        :
        <Button
          loading={loadingConsultation.sign}
          id="signButton"
          htmlType="submit"
          onClick={() => validateCompletedReport()}
          className="cmt-button tertiary"
          type="primary"
          size="large"
        >
          Firmar
        </Button>
    );
  }

  const psychologyOne = {
    CL: psychologicalOneCL(),
    PE: psychologicalOnePE(),
  };

  const psychologyTwo = {
    CL: psychologicalTwoCL(),
    PE: psychologicalTwoPE(),
  };

  const medicalSignType = {
    'health': health(),
    'psychologicalOne': psychologyOne[countryCode],
    'psychologicalTwo': psychologyTwo[countryCode],
  };

  return medicalSignType[medicalFormType];
};

export default SignButton;